import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

import { FormBuilder, FormGroup } from '@angular/forms';
import { AvailableLanguages, fullDateFormat, fullDateFormatTimestamp, normalizeLegalBasisOfStay } from '@constants';
import { isIosSafariBrowser } from '@core/services/browser-detection';
import { PROCESS_CATEGORY, PersonalDetails, UserProcess } from '@interfaces';
import { LayoutService } from '@layout/layout.service';
import { getPersonalDataFormGroupOptsLongResi } from '@shared/personal-data-form/LONG_RESI/form-fields.config';
import { getPersonalDataFormGroupOptsPermStay } from '@shared/personal-data-form/PERM_STAY/form-fields.config';
import { getPersonalDataFormGroupOptsTempPremit } from '@shared/personal-data-form/TEMP_PREMIT/form-fields.config';
import { getPersonalDataFormGroupOptsCukr } from '@shared/personal-data-form/CUKR/form-fields.config';
import { pick } from 'lodash-es';
import moment from 'moment-timezone';
import { defer, delay, map, merge, Observable, ReplaySubject, take, takeUntil } from 'rxjs';
import {
  personalDataBottomActionInFlowMaxHeight,
  personalDataBottomActionViewportCheckDelaysMs,
} from './personal-data-form.config';
import { ManagementFacade } from '@state/management/management.facade';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';

type DateFormats = {
  [key: string]: string;
};

@Component({
  selector: 'app-personal-data-form',
  templateUrl: './personal-data-form.component.html',
  styleUrls: ['./personal-data-form.component.scss'],
  standalone: false,
})
export class PersonalDataFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly layoutService = inject(LayoutService);
  private readonly fb = inject(FormBuilder);
  private readonly ngZone = inject(NgZone);
  private readonly managementFacade = inject(ManagementFacade);
  private readonly stackedMessages = inject(StackedMessagesService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);
  private bottomActionViewportCheckTimeouts: ReturnType<typeof setTimeout>[] = [];
  private readonly isIosSafari = isIosSafariBrowser({
    maxTouchPoints: window.navigator.maxTouchPoints,
    platform: window.navigator.platform,
    userAgent: window.navigator.userAgent,
    vendor: window.navigator.vendor,
  });
  private readonly visualViewportHandler = (): void => this.scheduleBottomActionLayoutCheck();
  private readonly windowResizeHandler = (): void => this.scheduleBottomActionLayoutCheck();

  public PROCESS_CATEGORY = PROCESS_CATEGORY;

  @Input() userProcess: UserProcess;
  @Input() currentLang: AvailableLanguages;
  @Input() loadingUserProcess: boolean;
  @Input() loadingPersonalDetails: boolean;
  @Input() errorMessage: string;
  @Input() mode: 'foreigner' | 'employee';

  @Output() updateDetails = new EventEmitter<PersonalDetails>();
  @Output() confirmDetails = new EventEmitter<void>();
  @Output() confirmVoivoChange = new EventEmitter<void>();
  @Output() toggleVerificationOfPersonalDetails = new EventEmitter();

  public correctlyFilledSteps: number = 0;
  public personalDataForm: FormGroup;
  public percentageOfCompletness: number = 0;
  public totalStepsInForm: number;
  public categoryForm: PROCESS_CATEGORY;
  public bottomActionBarInFlow: boolean = false;

  public loadingToggleMode: boolean = false;

  public datesFields = [
    'dateOfBirth',
    'lastEntryDate',
    'residenceVisaExpiryDate',
    'travelDocumentIssueDate',
    'travelDocumentExpiryDate',
    'stayVisaIssueDate',
    'stayVisaExpiryDate',
    'entryDocumentIssueDate',
    'entryDocumentExpiryDate',
    'stayEntitlingDocumentIssueDate',
    'spouseDob',
  ];

  public dateFormatsForFields: DateFormats = {
    ['dateOfBirth']: fullDateFormat,
    ['lastEntryDate']: fullDateFormat,
    ['residenceVisaExpiryDate']: fullDateFormat,
    ['travelDocumentIssueDate']: fullDateFormatTimestamp,
    ['travelDocumentExpiryDate']: fullDateFormatTimestamp,
    ['stayVisaIssueDate']: fullDateFormatTimestamp,
    ['stayVisaExpiryDate']: fullDateFormatTimestamp,
    ['entryDocumentIssueDate']: fullDateFormatTimestamp,
    ['entryDocumentExpiryDate']: fullDateFormatTimestamp,
    ['stayEntitlingDocumentIssueDate']: fullDateFormatTimestamp,
    ['spouseDob']: fullDateFormat,
  };

  public ngOnInit(): void {
    this.setupForm();
    this.checkBottomActionLayout();
  }

  public ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      window.visualViewport?.addEventListener('resize', this.visualViewportHandler);
      window.visualViewport?.addEventListener('scroll', this.visualViewportHandler);
      window.addEventListener('resize', this.windowResizeHandler);
    });

    if (this.mode !== 'foreigner') {
      setTimeout(() => {
        this.personalDataForm.disable({ onlySelf: false, emitEvent: false });
        this.refreshConsultantView();
      });
    }
  }

  public toggleFormEnabled(): void {
    if (this.loadingToggleMode) {
      return;
    }

    // Consultant is ENABLING edit mode -> refetch the latest personal details, then
    // fully rebuild the form so every nested sub-form re-inits from the fresh values.
    if (this.personalDataForm.disabled) {
      this.enableEditModeWithFreshData();
      return;
    }

    // Consultant is DISABLING edit mode -> just disable the current form.
    this.loadingToggleMode = true;
    setTimeout(() => {
      this.personalDataForm.disable({ onlySelf: false, emitEvent: false });
      this.loadingToggleMode = false;
    }, 200);
  }

  private enableEditModeWithFreshData(): void {
    const userProcessId = this.userProcess?.id;
    if (!userProcessId) {
      this.personalDataForm.enable({ onlySelf: false, emitEvent: false });
      return;
    }

    this.loadingToggleMode = true;

    this.fetchPersonalDetails(userProcessId).subscribe(freshDetails => {
      if (freshDetails) {
        this.rebuildFormWithDetails(freshDetails);
        return;
      }

      // On error keep the current values but still let the consultant edit.
      this.personalDataForm.enable({ onlySelf: false, emitEvent: false });
      this.loadingToggleMode = false;
    });
  }

  // Consultant just opened the read-only view -> re-fetch the latest personal details so
  // the displayed data is up to date, then rebuild the form and keep it disabled.
  private refreshConsultantView(): void {
    const userProcessId = this.userProcess?.id;
    if (!userProcessId) {
      this.personalDataForm.disable({ onlySelf: false, emitEvent: false });
      return;
    }

    this.loadingToggleMode = true;

    this.fetchPersonalDetails(userProcessId).subscribe(freshDetails => {
      if (freshDetails) {
        this.rebuildFormWithDetails(freshDetails, true);
        return;
      }

      // On error keep the initially loaded values but ensure the form stays read-only.
      this.personalDataForm.disable({ onlySelf: false, emitEvent: false });
      this.loadingToggleMode = false;
    });
  }

  // Pseudo-pure: triggers a fresh fetch for the given process and resolves once with the
  // freshly loaded personal details, or null when the fetch fails.
  private fetchPersonalDetails(userProcessId: string): Observable<PersonalDetails | null> {
    return defer(() => {
      this.managementFacade.getPersonalDetailsFullData(userProcessId);

      return merge(
        this.managementFacade.getPersonalDetailsFullDataSuccess$.pipe(map(({ personalDetails }) => personalDetails)),
        this.managementFacade.getPersonalDetailsFullDataError$.pipe(map((): PersonalDetails | null => null))
      );
    }).pipe(take(1), delay(200), takeUntil(this.destroy$));
  }

  // Pseudo-pure: applies freshly loaded details onto the process, tears down the child
  // form subtree and re-runs setup so every nested sub-form re-inits from fresh values.
  private rebuildFormWithDetails(personalDetails: PersonalDetails, disableAfter = false): void {
    this.userProcess = { ...this.userProcess, personalDetails };

    // Destroy the child form subtree so all nested sub-forms (steps, countries/previous
    // visits, etc.) are recreated and re-read the fresh values in their own ngOnInit.
    this.categoryForm = null;

    setTimeout(() => {
      this.setupForm();
      if (disableAfter) {
        this.personalDataForm.disable({ onlySelf: false, emitEvent: false });
      }
      this.loadingToggleMode = false;
    }, 10);
  }

  public ngOnDestroy(): void {
    window.visualViewport?.removeEventListener('resize', this.visualViewportHandler);
    window.visualViewport?.removeEventListener('scroll', this.visualViewportHandler);
    window.removeEventListener('resize', this.windowResizeHandler);
    this.clearBottomActionLayoutChecks();
    this.categoryForm = null;
    this.layoutService.showProgressBar$.next(false);
    this.layoutService.progressPercentage$.next(0);
    this.layoutService.showBottomFixedBar$.next(false);
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private setupForm(): void {
    let formGroup: FormGroup;

    const procCategory = this.userProcess?.process?.category;

    if (procCategory === PROCESS_CATEGORY.TEMP_PREMIT) {
      formGroup = getPersonalDataFormGroupOptsTempPremit(this.fb);
      this.totalStepsInForm = 5;
    }
    if (procCategory === PROCESS_CATEGORY.LONG_RESI) {
      formGroup = getPersonalDataFormGroupOptsLongResi(this.fb);
      this.totalStepsInForm = 6;
    }
    if (procCategory === PROCESS_CATEGORY.PERM_STAY) {
      formGroup = getPersonalDataFormGroupOptsPermStay(this.fb);
      this.totalStepsInForm = 6;
    }
    if (procCategory === PROCESS_CATEGORY.CUKR) {
      formGroup = getPersonalDataFormGroupOptsCukr(this.fb);
      this.totalStepsInForm = 3;
    }

    this.correctlyFilledSteps = 0;

    // Fill form with nested groups
    Object.values(formGroup.controls).forEach((stepGroup: any) => {
      const group = stepGroup as FormGroup;
      const keysInGroup = Object.keys(group.controls);
      const personalDetailsPickedProps = pick(this.userProcess.personalDetails, keysInGroup);

      if (procCategory === PROCESS_CATEGORY.TEMP_PREMIT && keysInGroup.includes('legalBasisOfStay')) {
        personalDetailsPickedProps.legalBasisOfStay = normalizeLegalBasisOfStay(
          personalDetailsPickedProps.legalBasisOfStay
        );
      }

      group.setValue(personalDetailsPickedProps);

      if (this.hasAnyFieldAnyValue(personalDetailsPickedProps)) {
        group.markAsTouched();
      }

      // Unfortunately each date field needs to be `.setValue` separately because of using moment
      this.datesFields.forEach((dateFieldName: string) => {
        if (keysInGroup.includes(dateFieldName)) {
          const fieldVal = this.userProcess.personalDetails[dateFieldName as keyof PersonalDetails] as string | null;
          const dateFormat: string | undefined = this.dateFormatsForFields[dateFieldName];
          if (fieldVal && dateFormat) {
            if (dateFormat) {
              group.get(dateFieldName).setValue(moment(fieldVal, dateFormat));
            }
          }
        }
      });

      if (group.valid) {
        this.correctlyFilledSteps += 1;
      }
    });

    this.personalDataForm = formGroup;
    this.categoryForm = this.userProcess.process.category;
    this.percentageOfCompletness = Math.ceil((this.correctlyFilledSteps / this.totalStepsInForm) * 100);
    setTimeout(() => {
      this.layoutService.progressPercentage$.next(this.percentageOfCompletness);
      this.layoutService.showProgressBar$.next(true);

      if (this.mode === 'foreigner') {
        this.layoutService.showBottomFixedBar$.next(true);
      }
    });
  }

  private hasAnyFieldAnyValue(personalDetailsPickedProps: Partial<PersonalDetails>): boolean {
    return Object.values(personalDetailsPickedProps).some(val => {
      if (Array.isArray(val) && val.length > 0) {
        return true;
      }

      return val !== '' && val !== undefined && val !== null;
    });
  }

  private scheduleBottomActionLayoutCheck(): void {
    this.clearBottomActionLayoutChecks();

    personalDataBottomActionViewportCheckDelaysMs.forEach(delayMs => {
      const timeout = setTimeout(() => this.checkBottomActionLayout(), delayMs);
      this.bottomActionViewportCheckTimeouts.push(timeout);
    });
  }

  private checkBottomActionLayout(): void {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const isMobileLayout = window.matchMedia('(max-width: 992px)').matches;
    const bottomActionBarInFlow =
      this.mode === 'foreigner' &&
      !this.isIosSafari &&
      isMobileLayout &&
      viewportHeight < personalDataBottomActionInFlowMaxHeight;

    if (this.bottomActionBarInFlow === bottomActionBarInFlow) {
      return;
    }

    this.ngZone.run(() => {
      this.bottomActionBarInFlow = bottomActionBarInFlow;
    });
  }

  private clearBottomActionLayoutChecks(): void {
    this.bottomActionViewportCheckTimeouts.forEach(timeout => clearTimeout(timeout));
    this.bottomActionViewportCheckTimeouts = [];
  }
}
